import { IntlShape } from 'react-intl';
import { ConsolidatedContact } from '../../../../../api/CoachingNotes/schema';
import { getCSCEndpoint } from '../../../../../config/Config';
import { renderUrl } from '../../../../../config/Url';
import { getAllMetrics } from '../../../../CoachingForm/CoachingDetails/Data';
import { CONSOLIDATED_CONTACT_CATEGORY } from '../../../../../Util/GlobalConstants';
import {
  getAllSecurityOptions,
  getAllVerticals,
  getAllBarRaisedQuestionOptions,
  getAllResolvedProblemOptions,
  getAllControllableOptions,
} from '../../../../CoachingForm/ContactReview/Data';
import { getLabelFromValue } from '../../../../CoachingForm/FieldComponents/Helpers';
import { DisplayObject } from '../../../../CoachingForm/LayoutComponents/FieldArrayDisplay';
import {
  isFeatureEnabled,
  Feature,
} from '../../../../../config/FeaturesConfig';
import moment from 'moment';

export const convertReviewToDisplay = (
  review: ConsolidatedContact,
  intl: IntlShape,
  showContactId: boolean = true,
  isTM: boolean = true
): DisplayObject[] => {
  const emptyDisplay: DisplayObject = { label: '', value: '' };

  const contactDisplay = showContactId
    ? {
        label: intl.formatMessage({
          id: 'cs_perfportal_contact_review_label_readonly_contact_id',
          defaultMessage: 'Contact ID:',
        }),
        value:
          review.marketplaceDomainName !== null &&
          review.marketplaceDomainName !== undefined
            ? {
                text: review.contactID,
                href: renderUrl(
                  // eslint-disable-next-line max-len
                  `/gp/stores/${review.marketplaceDomainName}/gp/communications/issue-manager?initialCommId=${review.contactID}`,
                  {
                    externalDomain: getCSCEndpoint(),
                  }
                ),
              }
            : review.contactID,
      }
    : emptyDisplay;

  const concessionFeatureEnabled = isFeatureEnabled(
    Feature.ENABLE_CONCESSION_COMPLIANCE
  );

  const showConcessionIcon =
    concessionFeatureEnabled &&
    isTM &&
    review.category === CONSOLIDATED_CONTACT_CATEGORY.CONCESSION_COMPLIANCE &&
    (!!review.processName || !!review.concessionFeedbackCommentText);

  const categoryDisplayPlain = {
    label: intl.formatMessage({
      id: 'cs_perfportal_contact_review_label_readonly_category',
      defaultMessage: 'Category:',
    }),
    value: review.category ?? '',
  };

  const categoryWithConcessionIconDisplay = {
    ...categoryDisplayPlain,
    value: {
      category: review.category ?? '',
      processName: review.processName ?? '',
      commentText: review.concessionFeedbackCommentText ?? '',
    },
  };

  const categoryDisplay: DisplayObject = showConcessionIcon
    ? categoryWithConcessionIconDisplay
    : concessionFeatureEnabled
      ? categoryDisplayPlain
      : emptyDisplay;

  const auditStartDisplay = !!review.auditStartDate
    ? {
        label: intl.formatMessage({
          id: 'cs_perfportal_contact_review_label_readonly_audit_date_start',
          defaultMessage: 'Audit Start',
        }),
        value: moment(review.auditStartDate).format('YYYY-MM-DD') ?? '',
      }
    : emptyDisplay;

  const auditEndDisplay = !!review.auditEndDate
    ? {
        label: intl.formatMessage({
          id: 'cs_perfportal_contact_review_label_readonly_audit_date_end',
          defaultMessage: 'Audit End',
        }),
        value: moment(review.auditEndDate).format('YYYY-MM-DD') ?? '',
      }
    : emptyDisplay;

  if (review.formReviews && review.formReviews.length > 0) {
    const displayObjects: DisplayObject[] = [];
    review.formReviews.forEach((form, index) => {
      if (
        form.formID === 'RECIPIENT_REVIEW_FORM' ||
        form.formID === 'OFFROAD_DRIVER_REVIEW_FORM'
      ) {
        if (auditStartDisplay !== emptyDisplay) {
          displayObjects.push(auditStartDisplay);
        }
        if (auditEndDisplay !== emptyDisplay) {
          displayObjects.push(auditEndDisplay);
        }
      }
      form.formAnswer.forEach(answer => {
        if (
          contactDisplay !== emptyDisplay &&
          answer.questionID.includes('CONTACT_ID')
        ) {
          displayObjects.push(contactDisplay);
          displayObjects.push(categoryDisplay);
        } else {
          const bothExist = answer.selectedOption && answer.answerText;

          const onlyAnswerText = !answer.selectedOption && answer.answerText;

          const displayItem = {
            label: intl.formatMessage({
              id: `${answer.questionText.translationID}`,
              defaultMessage: `${answer.questionText.defaultMessage}`,
            }),
            value: onlyAnswerText
              ? answer.answerText!
              : intl.formatMessage({
                  id: `${answer.selectedOption?.translationID}`,
                  defaultMessage: `${answer.selectedOption?.defaultMessage}`,
                }),
          };

          if (displayItem.value === 'undefined') {
            displayObjects.push(emptyDisplay);
          } else {
            displayObjects.push(displayItem);
          }

          if (bothExist) {
            const additionalNoteDisplay = {
              label: intl.formatMessage({
                id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
                defaultMessage: 'Additional notes:',
              }),
              value: answer.answerText ?? '',
            };

            displayObjects.push(additionalNoteDisplay);
          }

          if (answer.questionID.includes('CONTACT_ID')) {
            displayObjects.push(categoryDisplay);
          }
        }
      });
      // add a visual space between forms
      if (
        review.formReviews &&
        index < review.formReviews.length - 1 &&
        review.formReviews.length > 1
      ) {
        const formSpacer = {
          label: ' ',
          value: ' ',
        };
        displayObjects.push(formSpacer);
      }
    });

    return displayObjects;
  } else {
    const verticalDisplay = {
      label: intl.formatMessage({
        id: 'cs_perfportal_contact_review_label_readonly_vertical',
        defaultMessage: 'Vertical:',
      }),
      value: getLabelFromValue(review.contactInfo?.vertical, getAllVerticals()),
    };

    const relatedMetricDisplay = {
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_readonly_metric',
        defaultMessage: 'Metric:',
      }),
      value: getLabelFromValue(
        review.contactInfo?.relatedMetric,
        getAllMetrics()
      ),
    };

    const securityDisplay = {
      label: intl.formatMessage({
        id: 'cs_perfportal_contact_review_label_readonly_security_question',
        defaultMessage: 'Did the associate follow security policies?:',
      }),
      value: getLabelFromValue(
        review.contactInfo?.followedSecurityPolicies,
        getAllSecurityOptions(intl)
      ),
    };

    const noteDisplay = review.contactInfo?.content
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_contact_notes',
            defaultMessage: 'Contact notes:',
          }),
          value: review.contactInfo.content ?? '',
        }
      : emptyDisplay;

    const resolveDisplay = review.contactInfo?.resolvedProblem
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_resolve_problem_question',
            defaultMessage: 'Did the associate resolve the problem?:',
          }),
          value: getLabelFromValue(
            review.contactInfo?.resolvedProblem,
            getAllResolvedProblemOptions(intl)
          ),
        }
      : emptyDisplay;

    const controllableDisplay = review.contactInfo?.controllable
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_controllable_question',
            defaultMessage: 'Was it CSA controllable?:',
          }),
          value: getLabelFromValue(
            review.contactInfo?.controllable,
            getAllControllableOptions(intl)
          ),
        }
      : emptyDisplay;

    const uncontrollableContentDisplay = review.contactInfo?.uncontrollableText
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_uncontrollable_question',
            defaultMessage: 'Why?:',
          }),
          value: review.contactInfo.uncontrollableText ?? '',
        }
      : emptyDisplay;
    const greetDisplay = review.contactInfo?.greet?.status
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_greet',
            defaultMessage: 'Greet:',
          }),
          value: getLabelFromValue(
            review.contactInfo.greet.status,
            getAllBarRaisedQuestionOptions(intl)
          ),
        }
      : emptyDisplay;

    const greetNoteDisplay = review.contactInfo?.greet?.note
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
            defaultMessage: 'Additional notes:',
          }),
          value: review.contactInfo.greet.note ?? '',
        }
      : emptyDisplay;

    const verifySecurityDisplay = review.contactInfo?.verifySecurity?.status
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_verifySecurity',
            defaultMessage: 'Verify Security:',
          }),
          value: getLabelFromValue(
            review.contactInfo.verifySecurity.status,
            getAllBarRaisedQuestionOptions(intl)
          ),
        }
      : emptyDisplay;

    const veriifySecurityNoteDisplay = review.contactInfo?.verifySecurity?.note
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
            defaultMessage: 'Additional notes:',
          }),
          value: review.contactInfo.verifySecurity.note ?? '',
        }
      : emptyDisplay;

    const identifyProblemDisplay = review.contactInfo?.identifyProblem?.status
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_identifyProblem',
            defaultMessage: 'Identify Problem:',
          }),
          value: getLabelFromValue(
            review.contactInfo.identifyProblem.status,
            getAllBarRaisedQuestionOptions(intl)
          ),
        }
      : emptyDisplay;

    const identifyProblemNoteDisplay = review.contactInfo?.identifyProblem?.note
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
            defaultMessage: 'Additional notes:',
          }),
          value: review.contactInfo.identifyProblem.note ?? '',
        }
      : emptyDisplay;

    const acknowledgeDisplay = review.contactInfo?.acknowledge?.status
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_acknowledge',
            defaultMessage: 'Acknowledge:',
          }),
          value: getLabelFromValue(
            review.contactInfo.acknowledge.status,
            getAllBarRaisedQuestionOptions(intl)
          ),
        }
      : emptyDisplay;

    const acknowledgeNoteDisplay = review.contactInfo?.acknowledge?.note
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
            defaultMessage: 'Additional notes:',
          }),
          value: review.contactInfo.acknowledge.note ?? '',
        }
      : emptyDisplay;

    const researchMatchDisplay =
      review.contactInfo?.researchMatch?.status !== null &&
      review.contactInfo?.researchMatch?.status !== undefined
        ? {
            label: intl.formatMessage({
              id: 'cs_perfportal_contact_review_label_readonly_researchMatch',
              defaultMessage: 'Research And Match:',
            }),
            value: getLabelFromValue(
              review.contactInfo.researchMatch.status,
              getAllBarRaisedQuestionOptions(intl)
            ),
          }
        : emptyDisplay;

    const researchMatchNoteDisplay = review.contactInfo?.researchMatch?.note
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
            defaultMessage: 'Additional notes:',
          }),
          value: review.contactInfo.researchMatch.note ?? '',
        }
      : emptyDisplay;

    const solveDisplay = review.contactInfo?.solve?.status
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_solve',
            defaultMessage: 'Solve:',
          }),
          value: getLabelFromValue(
            review.contactInfo.solve.status,
            getAllBarRaisedQuestionOptions(intl)
          ),
        }
      : emptyDisplay;

    const solveNoteDisplay = review.contactInfo?.solve?.note
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
            defaultMessage: 'Additional notes:',
          }),
          value: review.contactInfo.solve.note ?? '',
        }
      : emptyDisplay;

    const closeDisplay = review.contactInfo?.close?.status
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_close',
            defaultMessage: 'Close:',
          }),
          value: getLabelFromValue(
            review.contactInfo.close.status,
            getAllBarRaisedQuestionOptions(intl)
          ),
        }
      : emptyDisplay;

    const closeNoteDisplay = review.contactInfo?.close?.note
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_additionalNotes',
            defaultMessage: 'Additional notes:',
          }),
          value: review.contactInfo.close.note ?? '',
        }
      : emptyDisplay;
    const additionalComment = review.contactInfo?.additionalComment
      ? {
          label: intl.formatMessage({
            id: 'cs_perfportal_contact_review_label_readonly_csa_improve_action_question',
            defaultMessage:
              'Generally, what could the CSA have done differently to improve this contact?:',
          }),

          value: review.contactInfo.additionalComment ?? '',
        }
      : emptyDisplay;

    return [
      contactDisplay,
      verticalDisplay,
      categoryDisplay,
      relatedMetricDisplay,
      securityDisplay,
      noteDisplay,
      resolveDisplay,
      controllableDisplay,
      uncontrollableContentDisplay,
      greetDisplay,
      greetNoteDisplay,
      verifySecurityDisplay,
      veriifySecurityNoteDisplay,
      identifyProblemDisplay,
      identifyProblemNoteDisplay,
      acknowledgeDisplay,
      acknowledgeNoteDisplay,
      researchMatchDisplay,
      researchMatchNoteDisplay,
      solveDisplay,
      solveNoteDisplay,
      closeDisplay,
      closeNoteDisplay,
      additionalComment,
    ];
  }
};
